import { useCallback, useState } from 'react';

import { SetReportToothConditionsUserDecisionReq } from '@/shared/api/protocol-ts/api/core/svc_report_pb';
import { Decision } from '@/shared/api/protocol-ts/model/dto_report_common_pb';
import { ConditionCode } from '@/shared/api/protocol-ts/model/dto_report_condition_codes_pb';
import { TOOTH_TYPE_CODES } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { ConditionDecisionChange, conditionModel } from '@/entities/condition';
import { organizationModel } from '@/entities/organization';
import { reportsModel, useCheckReportSignature } from '@/entities/reports';

import {
  getNegativeToothTypeConditionIDs,
  getParentConditionIDsToEnable,
} from '../lib';

export const useUpdateConditionDecision = (toothID: string) => {
  const [isLoading, setLoading] = useState(false);

  const { checkReportSignature } = useCheckReportSignature();
  const dispatch = useAppDispatch();
  const { isUnaided, isFDAVersion2 } = useAppSelector(
    organizationModel.selectors.selectFDAEnvironment,
  );
  const toothConditions = useAppSelector(
    conditionModel.selectors.selectByToothID(toothID),
  );

  const setToothTypeExclusively = useCallback(
    async (conditionCode: ConditionCode, conditionIDs?: string[]) => {
      const negativeConditionIDs = getNegativeToothTypeConditionIDs(
        toothConditions,
        conditionCode,
      );
      const positiveConditionIDs = conditionIDs ?? [];

      if (negativeConditionIDs.length > 0) {
        await dispatch(
          reportsModel.thunks.setReportToothConditionsUserDecision(
            new SetReportToothConditionsUserDecisionReq({
              PositiveDecisionConditionIDs: [],
              NegativeDecisionConditionIDs: negativeConditionIDs,
            }),
          ),
        ).unwrap();
      }

      if (positiveConditionIDs.length > 0) {
        await dispatch(
          reportsModel.thunks.setReportToothConditionsUserDecision(
            new SetReportToothConditionsUserDecisionReq({
              PositiveDecisionConditionIDs: positiveConditionIDs,
              NegativeDecisionConditionIDs: [],
            }),
          ),
        ).unwrap();

        return;
      }

      await dispatch(
        reportsModel.thunks.createReportToothConditions({
          ToothID: toothID,
          Conditions: [{ Code: conditionCode }],
        }),
      ).unwrap();
    },
    [toothConditions, toothID],
  );

  const updateConditionDecision: ConditionDecisionChange = useCallback(
    async ({
      conditionIDs,
      parentConditionId,
      parentConditionCode,
      conditionCode,
      userDecision,
    }) => {
      checkReportSignature({
        toothID,
        onSignatureChecked: async () => {
          const isToothTypeChange = TOOTH_TYPE_CODES.includes(conditionCode);

          if (
            isUnaided &&
            isToothTypeChange &&
            userDecision === Decision.PositiveDecision
          ) {
            setLoading(true);

            try {
              await setToothTypeExclusively(conditionCode, conditionIDs);
            } finally {
              setLoading(false);
            }

            return;
          }

          const selectedParentIDs = new Set(
            [
              parentConditionId,
              ...toothConditions
                .filter((condition) => conditionIDs?.includes(condition.ID))
                .map((condition) => condition.ParentID),
            ].filter(Boolean),
          );
          const parentConditions = toothConditions.filter(
            (condition) =>
              selectedParentIDs.has(condition.ID) ||
              (selectedParentIDs.size === 0 &&
                condition.Code === parentConditionCode),
          );
          const parentConditionIDs = getParentConditionIDsToEnable({
            isFDAVersion2,
            isUnaided,
            userDecision,
            parentConditions,
          });
          const shouldEnableParentCondition = parentConditionIDs.length > 0;
          const shouldCreateParentCondition =
            isFDAVersion2 &&
            userDecision === Decision.PositiveDecision &&
            !conditionIDs?.length &&
            parentConditionCode !== undefined &&
            parentConditionCode !== ConditionCode.ConditionCodeInvalidValue &&
            parentConditions.length === 0;

          if (
            isToothTypeChange ||
            shouldEnableParentCondition ||
            shouldCreateParentCondition
          ) {
            setLoading(true);
          }

          const requestData = new SetReportToothConditionsUserDecisionReq({
            PositiveDecisionConditionIDs: [],
            NegativeDecisionConditionIDs: [],
          });

          if (userDecision === Decision.PositiveDecision) {
            requestData.PositiveDecisionConditionIDs = [
              ...conditionIDs,
              ...parentConditionIDs,
            ];
          } else {
            requestData.NegativeDecisionConditionIDs = conditionIDs;
          }

          try {
            if (conditionIDs && conditionIDs.length) {
              await dispatch(
                reportsModel.thunks.setReportToothConditionsUserDecision(
                  requestData,
                ),
              );
            } else {
              if (
                conditionCode === undefined ||
                conditionCode === ConditionCode.ConditionCodeInvalidValue
              ) {
                return;
              }

              const parentCreationResponse = shouldCreateParentCondition
                ? await dispatch(
                    reportsModel.thunks.createReportToothConditions({
                      ToothID: toothID,
                      Conditions: [{ Code: parentConditionCode }],
                    }),
                  ).unwrap()
                : undefined;
              const resolvedParentConditionID = isFDAVersion2
                ? parentCreationResponse?.Conditions[0].ID ||
                  parentConditionId ||
                  parentConditions[0]?.ID ||
                  ''
                : parentConditionId ?? '';

              const result = await dispatch(
                reportsModel.thunks.createReportToothConditions({
                  ToothID: toothID,
                  Conditions: [
                    {
                      Code: conditionCode,
                      ParentConditionID: resolvedParentConditionID,
                    },
                  ],
                }),
              );

              if (
                reportsModel.thunks.createReportToothConditions.fulfilled.match(
                  result,
                ) &&
                shouldEnableParentCondition
              ) {
                await dispatch(
                  reportsModel.thunks.setReportToothConditionsUserDecision(
                    new SetReportToothConditionsUserDecisionReq({
                      PositiveDecisionConditionIDs: parentConditionIDs,
                    }),
                  ),
                );
              }
            }
          } finally {
            setLoading(false);
          }
        },
      });
    },
    [
      isUnaided,
      isFDAVersion2,
      toothID,
      toothConditions,
      setToothTypeExclusively,
    ],
  );

  return {
    updateConditionDecision,
    isLoading,
  };
};
